import { readdir } from "node:fs/promises";
import {
  basename,
  dirname,
  extname,
  isAbsolute,
  join,
  relative,
  resolve as pathResolve,
} from "node:path";

/**
 * Filesystem index of every `.md` file under a root.
 *
 * Provides two lookup paths:
 *
 * - `byBasename` — `name` (no `.md`) → list of absolute paths. Used for
 *   bare wiki links like `[[Foo]]`, which Obsidian resolves by filename
 *   anywhere in the vault.
 * - `byRelPath` — forward-slash relative path (no `.md`) → absolute path.
 *   Used for path-aware wiki links like `[[some-topic/Foo]]` or
 *   `[[../Foo]]`, where the operator wrote a path on purpose to
 *   disambiguate.
 *
 * `rootDir` is retained so resolvers can interpret root-relative paths.
 */
export interface FileIndex {
  readonly byBasename: ReadonlyMap<string, readonly string[]>;
  readonly byRelPath: ReadonlyMap<string, string>;
  /**
   * Optional lowercase rel-path → absolute-path map for case-insensitive
   * fallback. Present only when {@link buildFileIndex} was called with
   * `caseInsensitive: true`. If two rel-paths collide after lowercasing,
   * the first walker-visited path wins; the conflict is silent (rare in
   * a well-organized tree).
   */
  readonly byRelPathLower?: ReadonlyMap<string, string>;
  readonly rootDir: string;
}

/**
 * Options for {@link buildFileIndex}.
 */
export interface BuildIndexOptions {
  /**
   * Build an additional lowercase `byRelPathLower` map so {@link resolveLink}
   * can fall back to case-insensitive matching when the exact-case path
   * lookup fails. Useful when the operator's wiki-link convention uses
   * different casing than the on-disk layout (a frequent situation when
   * content is migrated from one vault into another with renamed roots,
   * e.g. `[[Some-Topic/foo]]` vs `data/some-topic/foo.md`).
   *
   * Default: false. Bare-name lookup is unaffected — Obsidian historically
   * treats bare names case-sensitively, and we keep that.
   */
  caseInsensitive?: boolean;
  /**
   * Extra file extensions (besides `.md`) to include in the index. Each
   * extension must include the leading dot, e.g. `[".hwp", ".docx", ".pdf"]`.
   *
   * `.md` files are always indexed with the extension stripped from their
   * key — `[[Foo]]` matches `Foo.md`. Files with one of the additional
   * extensions are indexed with the extension *kept*, so they only match
   * when the operator writes the full filename — `[[Foo.hwp]]` matches
   * `Foo.hwp`. This avoids surprise collisions where `[[Foo]]` would
   * suddenly match a non-markdown file.
   *
   * Default: `[]` (only `.md` is indexed; preserves v1 behavior).
   *
   * Note: only `.md` files are scanned for wiki links by
   * {@link import("./checker.js").checkDirectory} and rewritten by
   * {@link import("./rewrite.js").rewriteDirectory}. Additional extensions
   * affect *resolution targets* (what a wiki link can point at), not
   * *scan sources* (what a wiki link can appear in).
   */
  additionalExtensions?: readonly string[];
}

/**
 * Walk `rootDir` recursively and build a {@link FileIndex} of every `.md`
 * file (plus any `additionalExtensions` requested).
 *
 * Hidden directories (`.foo`) are skipped. `_foo` directories ARE indexed —
 * Obsidian wiki links can target any markdown file regardless of underscore
 * prefix, so we mirror that. A missing root returns an empty index.
 */
export async function buildFileIndex(
  rootDir: string,
  options: BuildIndexOptions = {},
): Promise<FileIndex> {
  const byBasename = new Map<string, string[]>();
  const byRelPath = new Map<string, string>();
  const additional = new Set(options.additionalExtensions ?? []);
  // Resolve to an absolute path once so the index honors its documented
  // absolute-path contract even when called with a relative rootDir
  // (a relative root would otherwise yield relative target paths).
  const root = pathResolve(rootDir);
  await walk(root, root, byBasename, byRelPath, additional);
  if (options.caseInsensitive) {
    const byRelPathLower = new Map<string, string>();
    for (const [rel, abs] of byRelPath) {
      const lower = rel.toLowerCase();
      if (!byRelPathLower.has(lower)) byRelPathLower.set(lower, abs);
    }
    return { byBasename, byRelPath, byRelPathLower, rootDir: root };
  }
  return { byBasename, byRelPath, rootDir: root };
}

async function walk(
  rootDir: string,
  dir: string,
  byBasename: Map<string, string[]>,
  byRelPath: Map<string, string>,
  additionalExts: ReadonlySet<string>,
): Promise<void> {
  let entries;
  try {
    entries = await readdir(dir, { withFileTypes: true });
  } catch (e) {
    if ((e as NodeJS.ErrnoException).code === "ENOENT") return;
    throw e;
  }
  // Sort by Unicode code units (NOT localeCompare, whose order varies by host
  // locale/ICU version — Korean and other non-ASCII filenames could sort
  // differently across machines). A deterministic walk order makes
  // byRelPathLower's first-wins collision resolution and byBasename's
  // candidate ordering reproducible regardless of the OS's readdir() order.
  entries.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  for (const dirent of entries) {
    const name = dirent.name;
    if (dirent.isDirectory()) {
      if (name.startsWith(".")) continue;
      await walk(rootDir, join(dir, name), byBasename, byRelPath, additionalExts);
      continue;
    }
    if (!dirent.isFile()) continue;
    const ext = extname(name);
    const isMd = ext === ".md";
    if (!isMd && !additionalExts.has(ext)) continue;
    const path = join(dir, name);
    // No stat() here: readdir(withFileTypes) already confirmed this is a
    // regular file via dirent.isFile() above (symlinks are excluded there
    // too), so a stat re-check could never change the outcome — it was a
    // redundant syscall.
    // `.md` strips its extension from keys; non-md files keep it so a
    // bare `[[Foo]]` cannot accidentally resolve to `Foo.hwp`.
    const key = isMd ? basename(name, ".md") : name;
    const list = byBasename.get(key);
    if (list) {
      list.push(path);
    } else {
      byBasename.set(key, [path]);
    }
    const relRaw = relative(rootDir, path).split(/[\\/]/).join("/");
    const rel = isMd ? relRaw.replace(/\.md$/, "") : relRaw;
    byRelPath.set(rel, path);
  }
}

/**
 * Resolution outcome for a single wiki link.
 *
 * - `unique`: one and only one file matched.
 * - `not-found`: no file matched.
 * - `ambiguous`: multiple files share the basename (only possible for
 *   bare-name links; path-aware links always resolve uniquely or fail).
 */
export type ResolveOutcome =
  | { kind: "unique"; path: string }
  | { kind: "not-found" }
  | { kind: "ambiguous"; candidates: readonly string[] };

/**
 * Optional hints that turn on path-aware resolution.
 *
 * - `sourcePath` — absolute path of the file the link was found in.
 *   Required to resolve `../foo`-style relative links.
 *
 * When neither hint is set, behavior collapses to bare-name lookup
 * (basename only), matching the original v1 semantics.
 */
export interface ResolveOpts {
  sourcePath?: string;
}

/**
 * Look up a wiki link in the index. Three cases:
 *
 * 1. **Bare name** (`[[Foo]]`) — basename lookup only. Returns `unique`
 *    if exactly one `Foo.md` exists, `ambiguous` if multiple, `not-found`
 *    otherwise.
 * 2. **Root-relative path** (`[[Projects/Foo]]`) — exact rel-path lookup
 *    against the index. Always `unique` or `not-found`.
 * 3. **Source-relative path** (`[[../Foo]]`, `[[./Foo]]`) — resolved
 *    against `dirname(sourcePath)` first, then looked up by rel-path.
 *    Requires `opts.sourcePath`; without it, returns `not-found`.
 *
 * Trailing `.md` in the link name is stripped before matching.
 */
export function resolveLink(
  name: string,
  index: FileIndex,
  opts: ResolveOpts = {},
): ResolveOutcome {
  // Strip any explicit .md extension the operator may have typed.
  const stripped = name.replace(/\.md$/u, "");

  if (!stripped.includes("/") && !stripped.includes("\\")) {
    // Case 1: bare name → basename lookup.
    const list = index.byBasename.get(stripped);
    if (!list || list.length === 0) return { kind: "not-found" };
    if (list.length === 1) return { kind: "unique", path: list[0]! };
    return { kind: "ambiguous", candidates: list };
  }

  const normalized = stripped.replace(/\\/g, "/");

  if (normalized.startsWith("../") || normalized.startsWith("./")) {
    // Case 3: source-relative path. Need a base to resolve from.
    if (!opts.sourcePath) return { kind: "not-found" };
    const baseDir = dirname(opts.sourcePath);
    const absolute = pathResolve(baseDir, normalized);
    const rel = relative(index.rootDir, absolute)
      .split(/[\\/]/)
      .join("/");
    // Guard: if the relative path climbs above rootDir, the link cannot
    // resolve within this index. Match only actual parent traversal
    // (`..` or `../…`), not in-root names that merely start with `..`
    // (e.g. `..draft.md`).
    if (rel === ".." || rel.startsWith("../") || isAbsolute(rel)) {
      return { kind: "not-found" };
    }
    return lookupRelPath(rel, index);
  }

  // Case 2: root-relative path.
  return lookupRelPath(normalized, index);
}

/**
 * Look up a rel-path with optional case-insensitive fallback.
 */
function lookupRelPath(rel: string, index: FileIndex): ResolveOutcome {
  const exact = index.byRelPath.get(rel);
  if (exact) return { kind: "unique", path: exact };
  if (index.byRelPathLower) {
    const fallback = index.byRelPathLower.get(rel.toLowerCase());
    if (fallback) return { kind: "unique", path: fallback };
  }
  return { kind: "not-found" };
}

/**
 * Reduce an absolute path to one relative to `rootDir`, using forward
 * slashes. Useful for reporting.
 */
export function toRel(path: string, rootDir: string): string {
  return relative(rootDir, path).split(/[\\/]/).join("/");
}
